import { EventEmitter, Injectable, Output } from "@angular/core";
import { GlobalTranslationVar } from "@commonClass/GlobalTranslationVar";
import { Language } from "@commonEnums/language";
import { isNullish } from "@commonHelpers/math-utils";
import { TranslateService } from "@commonNodeModules/@ngx-translate/core";
import { AccountLanguage } from "@interfaces/HttpClient/AccountApiPublicModels";
import { CatalogLanguage } from "@interfaces/HttpClient/CatalogApiPublicModels";
import { firstValueFrom } from "rxjs";
import { GlobalVarService } from "./global-var.service";

declare const CCM;
@Injectable({
	providedIn: "root"
})
export class TranslationService {
	private static languages = new Map<string, any>()
	private static currentLoadingLanguage?: Language;

	constructor(private translateService: TranslateService, private globalVarService: GlobalVarService
	) { }

	/*     formatMessage = formatMessage; */

	@Output() languageChanged = new EventEmitter();

	private async switchLanguage(language: Language) {
		TranslationService.currentLoadingLanguage = language;
		const translations = await firstValueFrom(this.translateService.use(language));
		TranslationService.currentLoadingLanguage = null;
		if (!TranslationService.languages.has(language) && TranslationService.currentLoadingLanguage !== language && typeof translations.___json_comment_global_var_area === "string") {
			TranslationService.languages.set(language, translations);
		}
		GlobalTranslationVar.SELECTED_LANGUAGE = language;
		this.languageChanged.emit(language);
		this.setCCM19Language(language);
		document.querySelector('html')?.setAttribute('lang', GlobalTranslationVar.SELECTED_LANGUAGE);

	}

	public setCCM19Language(language: Language) {
		try {
			if (!isNullish(CCM)) {
				this.switchCcm19Language(language);
			} else {
				const switchLanguageFunction = () => {
					this.switchCcm19Language(language);
					window.removeEventListener('ccm19WidgetLoaded', switchLanguageFunction);
				}
				window.addEventListener('ccm19WidgetLoaded', switchLanguageFunction);
			}
		} catch {

		}
	}

	private switchCcm19Language(language: Language) {
		if (language === Language.DE) {
			CCM?.switchLocale('de_DE')
		} else {
			CCM?.switchLocale('en_US')
		}
	}

	async switchCatalogLanguage(catalogLanguage: CatalogLanguage) {
		if (catalogLanguage === CatalogLanguage.German) {
			await this.switchLanguage(Language.DE);
		} else {
			await this.switchLanguage(Language.EN)
		}

	}

	async switchAdminLanguage(accountLanguage: AccountLanguage) {
		switch (accountLanguage) {
			case AccountLanguage.English: { await this.switchLanguage(Language.EN); break }
			case AccountLanguage.German: { await this.switchLanguage(Language.DE); break }
		}
	}

	private getLanguageKey(accountLanguage: AccountLanguage) {
		switch (accountLanguage) {
			case AccountLanguage.English: return Language.EN;
			case AccountLanguage.German: return Language.DE;
		}
	}

	getLanguage() {
		let lang = GlobalTranslationVar.SELECTED_LANGUAGE as string;

		if (!lang) {
			lang = Language.DE;
		}

		return lang.substring(0, 2).toLowerCase();
	}

	getByKey(key: string, params?) {
		if (params) {
			return this.translateService.instant(key, params);
		}
		return this.translateService.instant(key);
	}

	getByKeyAndLanguage(key: string, accountLanguage: AccountLanguage): string {
		const langKey = this.getLanguageKey(accountLanguage); // e.g., 'en', 'de'
		const translations = TranslationService.languages.get(langKey);

		if (!translations) {
			return '';
		}

		return this.resolveKey(translations, key) ?? '';

	}

	private resolveKey(obj: any, key: string): string | undefined {
		return key.split('.').reduce((acc, part) => acc?.[part], obj);
	}

	async loadLanguage(accountLanguage: AccountLanguage) {
		if (isNullish(accountLanguage)) {
			return "";
		}
		const translationKey = this.getLanguageKey(accountLanguage);
		if (!TranslationService.languages.has(translationKey) && TranslationService.currentLoadingLanguage !== translationKey) {
			TranslationService.currentLoadingLanguage = translationKey;
			const translations = await firstValueFrom(this.translateService.reloadLang(translationKey));
			TranslationService.currentLoadingLanguage = null;
			if (typeof translations.___json_comment_global_var_area === "string") {
				TranslationService.languages.set(translationKey, translations);
			}
		}
		return true;
	}

	async setDefaultLanguage() {
		await this.switchLanguage(Language.DE);
	}

	async initTranslation() {
		// bei user soll auch beim initalen aufruf die Sprache gesetzt werden. Da keine Id oder Unterteilung vorhanden sein kann muss in dem Fall der Slash weggelassen werden  
		const translationUrls = ["/administration/companies/",
			"/administration/catalog/",
			"/administration/user",
			"/onboarding",
			"/preview/"]

		const tmpTranslationUrls = ["/administration/companies/",
			"/watchlist/",
			"/search",
			"/international-catalogs",
			"/category",
			"/maintenance",
			"/a-z"]
		// Initialabfrage nach Benutzersprache 
		if (!isNullish(this.globalVarService.getLanguage()) && (translationUrls.some(e => window.location.pathname.startsWith(e)))) {
			await this.switchAdminLanguage(this.globalVarService.getLanguage());
			// Initialabfrage nach derzeit eingestellter Spache über den Header 
		} else if (!isNullish(this.globalVarService.getTmpLanguage()) && (window.location.pathname === "/" || tmpTranslationUrls.some(e => window.location.pathname.startsWith(e)))) {
			await this.switchAdminLanguage(this.globalVarService.getTmpLanguage());
		} else {
			await this.setDefaultLanguage();
		}
	}
}
